import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Beaker,
  Bot,
  BrainCircuit,
  ChevronRight,
  Database,
  Eye,
  Filter,
  FlaskConical,
  LoaderCircle,
  Play,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { explainIntelligenceInsight } from "@/components/admin/adminApi";
import {
  analyzeSyntheticDataset,
  buildDeterministicNarrative,
  buildNarrativePayload,
} from "./analyticsEngine";
import {
  METRIC_REGISTRY,
  formatMetricDelta,
  formatMetricValue,
} from "./metricRegistry";
import {
  SYNTHETIC_SCENARIOS,
  generateSyntheticDataset,
  normalizeSyntheticConfig,
} from "./syntheticData";

const DEFAULT_CONFIG = Object.freeze({
  scenario: "assessment_dropoff",
  participants: 360,
  days: 90,
  programCount: 3,
  seed: 2048,
});

const RELEASE_1_SYNTHETIC_ANCHOR = "2026-08-26T12:00:00.000Z";

const severityStyles = {
  high: { backgroundColor: "#FDECEC", color: "#8A3434", label: "High priority" },
  medium: { backgroundColor: "#FFF4DA", color: "#7C5B16", label: "Medium priority" },
  watch: { backgroundColor: "var(--forr-teal-tint)", color: "var(--forr-teal-deep)", label: "Watch" },
};

const formatPercent = (value) =>
  Number.isFinite(value) ? `${Math.round(value * 100)}%` : "—";

const datasetFrom = (config) =>
  generateSyntheticDataset({
    ...config,
    anchorDate: RELEASE_1_SYNTHETIC_ANCHOR,
  });

const LabStatCard = ({ metric, current, comparison }) => {
  const change =
    Number.isFinite(current?.value) && Number.isFinite(comparison?.value)
      ? current.value - comparison.value
      : null;
  const improving = Number(change) >= 0;
  return (
    <article
      className="rounded-[24px] p-5 forr-surface"
      style={{ border: "1px solid var(--forr-line-soft)" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] forr-text-mute">
            {metric.name}
          </div>
          <div className="mt-2 font-display text-3xl forr-text-ink">
            {formatMetricValue(metric, current?.value)}
          </div>
        </div>
        <div
          className="flex h-10 w-10 items-center justify-center rounded-2xl"
          style={{ backgroundColor: improving ? "var(--forr-teal-tint)" : "#FDECEC" }}
        >
          {improving ? (
            <TrendingUp size={18} className="forr-text-teal-deep" />
          ) : (
            <TrendingDown size={18} style={{ color: "#A34F4F" }} />
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 text-xs">
        <span className="forr-text-mute">
          Prior {formatMetricValue(metric, comparison?.value)}
        </span>
        <span style={{ color: improving ? "#2F7D5A" : "#A34F4F" }}>
          {formatMetricDelta(metric, change)}
        </span>
      </div>
      <div className="mt-3 text-[10px] forr-text-mute">
        n={current?.sampleSize ?? 0} · {metric.calculationVersion}
      </div>
    </article>
  );
};

const DemoControls = ({ config, onChange, onGenerate, onReset, dataset }) => (
  <aside
    className="rounded-[28px] p-5 forr-surface sm:p-6"
    style={{ border: "1px solid var(--forr-line-soft)" }}
  >
    <div className="flex items-center gap-3">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: "var(--forr-teal-tint)" }}
      >
        <FlaskConical size={20} className="forr-text-teal-deep" />
      </div>
      <div>
        <div className="forr-overline">Demo controls</div>
        <h2 className="mt-1 font-display text-2xl forr-text-ink">Synthetic scenario</h2>
      </div>
    </div>

    <label className="mt-6 block text-xs font-medium forr-text-ink">
      Prepared anomaly
      <select
        value={config.scenario}
        onChange={(event) => onChange("scenario", event.target.value)}
        className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm forr-text-ink"
      >
        {SYNTHETIC_SCENARIOS.map((scenario) => (
          <option value={scenario.key} key={scenario.key}>
            {scenario.label}
          </option>
        ))}
      </select>
    </label>
    <p className="mt-2 text-xs leading-relaxed forr-text-mute">
      {SYNTHETIC_SCENARIOS.find((scenario) => scenario.key === config.scenario)?.description}
    </p>

    <div className="mt-5 grid grid-cols-2 gap-3">
      <label className="text-xs font-medium forr-text-ink">
        Participants
        <input
          type="number"
          min="80"
          max="1200"
          step="20"
          value={config.participants}
          onChange={(event) => onChange("participants", event.target.value)}
          className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
        />
      </label>
      <label className="text-xs font-medium forr-text-ink">
        Date range
        <select
          value={config.days}
          onChange={(event) => onChange("days", event.target.value)}
          className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
        >
          {[60, 90, 120, 180].map((days) => (
            <option key={days} value={days}>{days} days</option>
          ))}
        </select>
      </label>
      <label className="text-xs font-medium forr-text-ink">
        Programs
        <select
          value={config.programCount}
          onChange={(event) => onChange("programCount", event.target.value)}
          className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
        >
          {[1, 2, 3, 4].map((count) => (
            <option key={count} value={count}>{count}</option>
          ))}
        </select>
      </label>
      <label className="text-xs font-medium forr-text-ink">
        Random seed
        <input
          type="number"
          min="1"
          max="999999"
          value={config.seed}
          onChange={(event) => onChange("seed", event.target.value)}
          className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
        />
      </label>
    </div>

    <div className="mt-5 grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
      <button
        type="button"
        onClick={onGenerate}
        className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold forr-btn-primary"
      >
        <Play size={16} /> Generate dataset
      </button>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium forr-btn-secondary"
      >
        <RefreshCw size={16} /> Reset demo
      </button>
    </div>

    <div className="mt-6 rounded-2xl p-4 forr-surface-tint">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs forr-text-mute">Dataset</span>
        <span className="rounded-full bg-white/75 px-3 py-1 text-[9px] uppercase tracking-[0.13em] forr-text-teal-deep">
          Synthetic only
        </span>
      </div>
      <div className="mt-3 font-display text-xl forr-text-ink">
        {dataset.records.length.toLocaleString()} records
      </div>
      <div className="mt-2 break-all text-[10px] forr-text-mute">{dataset.id}</div>
    </div>
  </aside>
);

const InsightCard = ({ insight, selected, onSelect }) => {
  const style = severityStyles[insight.severity] || severityStyles.watch;
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full rounded-[22px] p-5 text-left transition"
      style={{
        border: selected ? "1px solid var(--forr-teal)" : "1px solid var(--forr-line-soft)",
        backgroundColor: selected ? "var(--forr-teal-tint)" : "rgba(255,255,255,0.78)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className="rounded-full px-3 py-1 text-[9px] font-medium uppercase tracking-[0.12em]"
          style={{ backgroundColor: style.backgroundColor, color: style.color }}
        >
          {style.label}
        </span>
        <ChevronRight size={16} className="forr-text-mute" />
      </div>
      <h3 className="mt-4 font-display text-xl forr-text-ink">{insight.title}</h3>
      <p className="mt-2 text-xs leading-relaxed forr-text-soft">{insight.statement}</p>
      <div className="mt-4 flex flex-wrap gap-2 text-[10px] forr-text-mute">
        <span>n={insight.sampleSize}</span>
        <span>·</span>
        <span>{insight.confidence} confidence</span>
        <span>·</span>
        <span>Observed change</span>
      </div>
    </button>
  );
};

const ContributionBranch = ({ branch, metric }) => (
  <details
    className="rounded-2xl forr-surface-tint"
    open={branch.contributionShare >= 0.45}
  >
    <summary className="cursor-pointer list-none p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] forr-text-mute">
            {branch.dimensionLabel}
          </div>
          <div className="mt-1 text-sm font-medium forr-text-ink">{branch.label}</div>
        </div>
        <div className="text-right">
          <div className="font-display text-xl forr-text-ink">
            {formatPercent(branch.contributionShare)}
          </div>
          <div className="text-[9px] forr-text-mute">of modeled contribution</div>
        </div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/80">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.max(3, branch.contributionShare * 100)}%`,
            backgroundColor: "var(--forr-teal)",
          }}
        />
      </div>
      <div className="mt-3 text-[10px] forr-text-mute">
        {formatMetricValue(metric, branch.comparisonValue)} → {formatMetricValue(metric, branch.currentValue)} · n={branch.sampleSize}
      </div>
    </summary>
    {branch.children.length > 0 && (
      <div className="space-y-2 border-t px-4 pb-4 pt-3" style={{ borderColor: "var(--forr-line-soft)" }}>
        {branch.children.map((child) => (
          <div key={child.id} className="rounded-xl bg-white/75 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[9px] uppercase tracking-[0.12em] forr-text-mute">{child.dimensionLabel}</div>
                <div className="mt-1 text-xs font-medium forr-text-ink">{child.label}</div>
              </div>
              <div className="text-right text-xs forr-text-soft">
                {formatPercent(child.contributionShare)} · n={child.sampleSize}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </details>
);

const EvidenceTable = ({ evidence, metric }) => (
  <div className="mt-5 overflow-hidden rounded-2xl" style={{ border: "1px solid var(--forr-line-soft)" }}>
    <div className="flex items-center justify-between border-b bg-white/70 px-4 py-3" style={{ borderColor: "var(--forr-line-soft)" }}>
      <div className="text-xs font-medium forr-text-ink">Supporting aggregate evidence</div>
      <div className="text-[9px] uppercase tracking-[0.13em] forr-text-mute">No personal identifiers</div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[650px] text-left text-xs">
        <thead>
          <tr className="text-[9px] uppercase tracking-[0.12em] forr-text-mute">
            {["Dimension", "Segment", "Prior", "Current", "Contribution", "Sample"].map((label) => (
              <th key={label} className="border-b px-4 py-3 font-medium" style={{ borderColor: "var(--forr-line-soft)" }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {evidence.map((item) => (
            <tr key={item.id} className="border-b last:border-0" style={{ borderColor: "var(--forr-line-soft)" }}>
              <td className="px-4 py-3 forr-text-mute">{item.dimension}</td>
              <td className="px-4 py-3 font-medium forr-text-ink">{item.segment}</td>
              <td className="px-4 py-3 forr-text-soft">{formatMetricValue(metric, item.comparisonValue)}</td>
              <td className="px-4 py-3 forr-text-soft">{formatMetricValue(metric, item.currentValue)}</td>
              <td className="px-4 py-3 forr-text-soft">{formatPercent(item.contributionShare)}</td>
              <td className="px-4 py-3 forr-text-soft">{item.sampleSize}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const NarrativePanel = ({ insight, rca, agentEnabled }) => {
  const fallback = useMemo(() => buildDeterministicNarrative(insight, rca), [insight, rca]);
  const [narrative, setNarrative] = useState(fallback);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    setNarrative(fallback);
    setError("");
  }, [fallback]);

  const runAgent = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await explainIntelligenceInsight(buildNarrativePayload(insight, rca));
      setNarrative({ ...result.narrative, source: "grounded-ai" });
    } catch (requestError) {
      setNarrative(fallback);
      setError(`${requestError.message} The validated deterministic explanation remains available.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="rounded-[26px] p-5 forr-panel-action sm:p-6"
      style={{ border: "1px solid var(--forr-action-border)" }}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80">
            <Bot size={20} className="forr-text-teal-deep" />
          </div>
          <div>
            <div className="forr-overline">
              {agentEnabled ? "Insights Agent" : "Release 1 explanation"}
            </div>
            <h3 className="mt-1 font-display text-2xl forr-text-ink">
              {agentEnabled ? "Grounded explanation" : "Deterministic explanation"}
            </h3>
          </div>
        </div>
        {agentEnabled && (
          <button
            type="button"
            onClick={runAgent}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold forr-btn-primary disabled:opacity-50"
          >
            {loading ? <LoaderCircle size={15} className="animate-spin" /> : <Sparkles size={15} />}
            Run Insights Agent
          </button>
        )}
      </div>
      <div className="mt-5 rounded-2xl bg-white/70 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.12em] forr-surface-tint forr-text-teal-deep">
            {narrative.source === "grounded-ai" ? "AI summary · validated payload" : "Deterministic summary"}
          </span>
          <span className="text-[10px] forr-text-mute">Calculations completed before explanation</span>
        </div>
        <h4 className="mt-4 font-display text-xl forr-text-ink">{narrative.headline}</h4>
        <p className="mt-3 text-sm leading-relaxed forr-text-soft">{narrative.explanation}</p>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl p-4 forr-surface-tint">
            <div className="text-[10px] uppercase tracking-[0.13em] forr-text-mute">Investigate next</div>
            <p className="mt-2 text-xs leading-relaxed forr-text-ink">{narrative.nextInvestigation}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ backgroundColor: "#FFF8E9" }}>
            <div className="text-[10px] uppercase tracking-[0.13em]" style={{ color: "#7C5B16" }}>Analytical limitation</div>
            <p className="mt-2 text-xs leading-relaxed forr-text-soft">{narrative.limitation}</p>
          </div>
        </div>
      </div>
      {error && <p className="mt-3 text-xs text-red-800" role="alert">{error}</p>}
    </section>
  );
};

const EmptyInsights = () => (
  <div className="rounded-[24px] p-8 text-center forr-surface" style={{ border: "1px solid var(--forr-line-soft)" }}>
    <Target size={28} className="mx-auto forr-text-mute" />
    <h3 className="mt-4 font-display text-xl forr-text-ink">No threshold-crossing insight</h3>
    <p className="mt-2 text-sm forr-text-soft">Change the seed or select a prepared anomaly to exercise the detection workflow.</p>
  </div>
);

const PortfolioBanner = () => (
  <div className="mb-6 flex flex-col gap-3 rounded-[24px] p-5 forr-panel-action sm:flex-row sm:items-center sm:justify-between">
    <div className="flex items-start gap-3">
      <Eye size={20} className="mt-0.5 forr-text-teal-deep" />
      <div>
        <div className="text-sm font-medium forr-text-ink">Portfolio presentation mode</div>
        <p className="mt-1 text-xs forr-text-soft">Synthetic data only. Internal participant controls and personal records are not part of this view.</p>
      </div>
    </div>
    <span className="rounded-full bg-white/80 px-4 py-2 text-[9px] uppercase tracking-[0.14em] forr-text-teal-deep">Sanitized demonstration</span>
  </div>
);

const IntelligenceLab = ({ onBack, agentEnabled = false }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [dataset, setDataset] = useState(() => datasetFrom(DEFAULT_CONFIG));
  const [selectedInsightId, setSelectedInsightId] = useState(null);
  const [portfolioMode, setPortfolioMode] = useState(false);
  const analysis = useMemo(() => analyzeSyntheticDataset(dataset), [dataset]);
  const selectedInsight =
    analysis.insights.find((insight) => insight.id === selectedInsightId) || analysis.insights[0] || null;

  const changeConfig = (key, value) => {
    setConfig((previous) => normalizeSyntheticConfig({ ...previous, [key]: value }));
  };

  const generate = () => {
    const normalized = normalizeSyntheticConfig(config);
    setConfig(normalized);
    setDataset(datasetFrom(normalized));
    setSelectedInsightId(null);
  };

  const reset = () => {
    setConfig(DEFAULT_CONFIG);
    setDataset(datasetFrom(DEFAULT_CONFIG));
    setSelectedInsightId(null);
  };

  const visibleMetrics = METRIC_REGISTRY.filter((metric) =>
    [
      "assessment_started_count",
      "assessment_completion_rate",
      "intervention_completion_rate",
      "follow_up_completion_rate",
      "positive_response_rate",
    ].includes(metric.key),
  );

  return (
    <div data-testid="apos-intelligence-lab">
      {portfolioMode && <PortfolioBanner />}
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-xs font-medium forr-text-teal-deep">
            <ArrowLeft size={15} /> Back to APOS Admin
          </button>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.14em] forr-surface-tint forr-text-teal-deep">Release 1</span>
            <span className="rounded-full px-3 py-1 text-[9px] uppercase tracking-[0.14em]" style={{ backgroundColor: "#EAF0FF", color: "#38558D" }}>Synthetic only</span>
          </div>
          <h1 className="mt-4 font-display text-4xl tracking-[-0.04em] forr-text-ink sm:text-5xl">APOS Intelligence Lab</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed forr-text-soft sm:text-base">
            Detect important metric changes, decompose the segments contributing to them, inspect supporting aggregate evidence, and generate a grounded explanation without opening production participant data.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPortfolioMode((value) => !value)}
          className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium forr-btn-secondary"
        >
          <Eye size={16} /> {portfolioMode ? "Exit portfolio mode" : "Portfolio mode"}
        </button>
      </div>

      <div className="mt-7 flex flex-wrap gap-3 rounded-[22px] p-4 forr-surface" style={{ border: "1px solid var(--forr-line-soft)" }}>
        <div className="inline-flex items-center gap-2 text-xs forr-text-ink"><ShieldCheck size={16} className="forr-text-teal-deep" /> Owner-authorized route</div>
        <div className="inline-flex items-center gap-2 text-xs forr-text-ink"><Database size={16} className="forr-text-teal-deep" /> No production query</div>
        <div className="inline-flex items-center gap-2 text-xs forr-text-ink"><Beaker size={16} className="forr-text-teal-deep" /> Reproducible seed</div>
        <div className="inline-flex items-center gap-2 text-xs forr-text-ink"><BrainCircuit size={16} className="forr-text-teal-deep" /> Deterministic analysis first</div>
      </div>

      <section className="mt-7">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="forr-overline">Executive overview</div>
            <h2 className="mt-2 font-display text-3xl forr-text-ink">Current period performance</h2>
          </div>
          <div className="hidden items-center gap-2 text-xs forr-text-mute sm:flex"><Filter size={15} /> Current half vs comparison half</div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {visibleMetrics.map((metric) => (
            <LabStatCard key={metric.key} metric={metric} current={analysis.current[metric.key]} comparison={analysis.comparison[metric.key]} />
          ))}
        </div>
      </section>

      <div className="mt-8 grid gap-6 xl:grid-cols-[330px_minmax(0,1fr)]">
        {!portfolioMode && (
          <DemoControls config={config} onChange={changeConfig} onGenerate={generate} onReset={reset} dataset={dataset} />
        )}
        <section className={portfolioMode ? "xl:col-span-2" : ""}>
          <div className="flex items-center gap-3">
            <BarChart3 size={20} className="forr-text-teal-deep" />
            <div>
              <div className="forr-overline">Insights feed</div>
              <h2 className="mt-1 font-display text-3xl forr-text-ink">Changes requiring attention</h2>
            </div>
          </div>
          <div className="mt-5 grid gap-3 lg:grid-cols-2">
            {analysis.insights.length ? analysis.insights.map((insight) => (
              <InsightCard
                key={insight.id}
                insight={insight}
                selected={selectedInsight?.id === insight.id}
                onSelect={() => setSelectedInsightId(insight.id)}
              />
            )) : <EmptyInsights />}
          </div>
        </section>
      </div>

      {selectedInsight && (
        <section className="mt-8 grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
          <div className="rounded-[28px] p-5 forr-surface sm:p-6" style={{ border: "1px solid var(--forr-line-soft)" }}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="forr-overline">Root-Cause Explorer</div>
                <h2 className="mt-2 font-display text-3xl forr-text-ink">{selectedInsight.title}</h2>
                <p className="mt-3 text-sm leading-relaxed forr-text-soft">{selectedInsight.statement}</p>
              </div>
              <div className="rounded-2xl p-4 text-right forr-surface-tint">
                <div className="text-[9px] uppercase tracking-[0.13em] forr-text-mute">Confidence</div>
                <div className="mt-1 font-display text-xl capitalize forr-text-ink">{selectedInsight.confidence}</div>
                <div className="mt-1 text-[10px] forr-text-mute">n={selectedInsight.sampleSize}</div>
              </div>
            </div>

            <div className="mt-6 rounded-2xl p-4" style={{ backgroundColor: "#FFF8E9", border: "1px solid #F0DFC0" }}>
              <div className="flex items-start gap-3">
                <AlertTriangle size={17} style={{ color: "#7C5B16" }} />
                <div>
                  <div className="text-xs font-medium" style={{ color: "#6B4E12" }}>Contribution is not causation</div>
                  <p className="mt-1 text-xs leading-relaxed forr-text-soft">{selectedInsight.rca.analyticalBoundary}</p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium forr-text-ink">Expandable contribution tree</div>
                <div className="text-[10px] forr-text-mute">Decomposed by {selectedInsight.rca.root.dimensionLabel.toLowerCase()}</div>
              </div>
              <div className="mt-3 space-y-3">
                {selectedInsight.rca.root.children.map((branch) => (
                  <ContributionBranch key={branch.id} branch={branch} metric={selectedInsight.rca.metric} />
                ))}
              </div>
            </div>
            <EvidenceTable evidence={selectedInsight.rca.evidence} metric={selectedInsight.rca.metric} />
          </div>
          <NarrativePanel
            key={selectedInsight.id}
            insight={selectedInsight}
            rca={selectedInsight.rca}
            agentEnabled={agentEnabled}
          />
        </section>
      )}

      <section className="mt-8 rounded-[26px] p-5 forr-surface sm:p-6" style={{ border: "1px solid var(--forr-line-soft)" }}>
        <div className="flex items-start gap-3">
          <Users size={20} className="mt-0.5 forr-text-teal-deep" />
          <div>
            <div className="forr-overline">Release boundary</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">No client or production behavior changes</h2>
            <p className="mt-3 max-w-4xl text-sm leading-relaxed forr-text-soft">
              This release generates coherent fictional records in browser memory. It does not read participant vaults, alter APOS scoring, modify recommendations, or claim real intervention outcomes. Production analytics connection belongs to Release 2.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IntelligenceLab;
